// @flow

import * as React from 'react';
import { superpathjoin } from 'superpathjoin';

import type { TranslatorProps } from '@gandi/translate';
import { Base } from '@gandi/ui';
import {
  Action,
  ListCell,
  ListItem,
  ListRow,
  TitleCell,
} from '@gandi/ui/deprecated';
import { Intro } from 'gandi.react-public-layout';

import type { WithRouterProps } from 'src/helpers/withRouter';
import type { PublicUrls } from 'src/types';

import Layout from '../../../../containers/Layout';
import type { Subject } from '../../types';
import DocumentationLinks from '../DocumentationLinks';
import styles from './Success.scss';

export type StateProps = {|
  locale: string,
  publicUrls: PublicUrls,
  corporateUrl: string,
  publicSite: string,
  searchDomainUrl: string,
|};

export type DecoratedProps = {|
  ...WithRouterProps<{|
    category?: string,
    subject?: Subject,
    topic?: string,
  |}>,
  translator: TranslatorProps,
|};

export type Props = {| ...StateProps, ...DecoratedProps |};

export function Success(props: Props): React.Element<typeof Layout> {
  const dashboardUrl = superpathjoin(
    props.publicUrls.adminUrl,
    props.locale,
    'dashboard'
  );

  const {
    translator: { __ },
    corporateUrl,
    publicSite,
    searchDomainUrl,
  } = props;

  const searchParams = props.location.search;
  const dpoContact = 'my-rights' in props.location.query;
  const isCorporate = 'corporate-contact' in props.location.query;
  const standaloneContact = !!searchParams.match(/-contact$/);
  const supportContact = !searchParams.match(/-/);

  return !isCorporate ? (
    <Layout location={props.location}>
      <Base marginBottom="base">
        <ListRow separatorBottom>
          <ListItem>
            <TitleCell
              icon={supportContact ? 'support' : 'success'}
              iconTheme="green"
            >
              {/* eslint-disable-next-line */}
              {dpoContact
                ? __("Thank you for contacting Gandi's data protection team")
                : standaloneContact
                ? __('Thank you for contacting us')
                : __("Thank you for contacting Gandi's Customer Care team")}
            </TitleCell>
          </ListItem>
        </ListRow>
        {supportContact && (
          <Base
            padding="small"
            backgroundColor="grey-lighter"
            separatorTop
            className={styles.drawer}
          >
            <p>
              {__(
                'You will soon receive a confirmation email containing a ticket ' +
                  'number for your reference.'
              )}
            </p>
            <p>
              {__(
                'You may reply to this email with additional information or reply ' +
                  'to this email if your issue has been resolved in the meantime.'
              )}
            </p>
            <p>
              {__(
                'Our support team will reply to your question as soon as possible.'
              )}
            </p>
          </Base>
        )}
        {dpoContact && (
          <Base
            padding="small"
            backgroundColor="grey-lighter"
            separatorTop
            className={styles.drawer}
          >
            <p>
              {__(
                'Your request has been sent to our DPO (Grant Thornton) ' +
                  'and to our legal department. ' +
                  'It will be processed as soon as possible, and at most within a month.'
              )}
            </p>
          </Base>
        )}
        {standaloneContact && (
          <Base
            padding="small"
            backgroundColor="grey-lighter"
            separatorTop
            className={styles.drawer}
          >
            <p>{__('We will get back to you as soon as possible.')}</p>
          </Base>
        )}
      </Base>
      {supportContact && (
        <Base marginBottom="base" key="manage-products">
          <ListRow>
            <ListItem>
              <TitleCell>{__('Manage your products')}</TitleCell>
            </ListItem>
            <Action icon="arrowRight" variant="wide" href={dashboardUrl}>
              {__('Dashboard')}
            </Action>
          </ListRow>
        </Base>
      )}
      {supportContact && (
        <Base marginBottom="base" key="learn-more">
          <DocumentationLinks
            title={__('Learn more with our Documentation')}
            translator={props.translator}
          />
        </Base>
      )}
      {supportContact && (
        <Base marginBottom="base" key="see-status">
          <ListRow>
            <ListItem>
              <ListCell>
                {__('You can see the status of our services 24/7')}
              </ListCell>
            </ListItem>
            <Action
              icon="arrowRight"
              variant="wide"
              href="http://status.gandi.net/"
            >
              {__('See Status')}
            </Action>
          </ListRow>
        </Base>
      )}
    </Layout>
  ) : (
    <Layout location={props.location} className={styles.corporateLayout}>
      <Base marginBottom="base">
        <Intro
          title={__('Contact Gandi Corporate Services')}
          className={styles.corporateTitle}
        >
          <Base className={styles.alert}>
            <p>
              {__(
                "Your message to Gandi Corporate Services' team has been sent."
              )}
            </p>
            <p>
              {__(
                'An agent will get in touch with you as soon as possible. You will receive an email at the address you entered in the form.'
              )}
            </p>
          </Base>
        </Intro>

        <ul className={styles.corpoLinks}>
          <li>
            <Action
              href={corporateUrl}
              icon="arrowRight"
              variant="block"
              className={styles.corpoLinkItem}
            >
              {__('Gandi Corporate Services')}
            </Action>
          </li>
          <li>
            <Action
              href={publicSite}
              icon="arrowRight"
              variant="block"
              className={styles.corpoLinkItem}
            >
              {__('Gandi.net')}
            </Action>
          </li>
          <li>
            <Action
              href={dashboardUrl}
              icon="arrowRight"
              variant="block"
              className={styles.corpoLinkItem}
            >
              {__('Login to dashboard')}
            </Action>
          </li>
          <li>
            <Action
              href={searchDomainUrl}
              icon="arrowRight"
              variant="block"
              className={styles.corpoLinkItem}
            >
              {__('Search domains')}
            </Action>
          </li>
        </ul>
      </Base>
    </Layout>
  );
}

export default Success;
